import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getMyOsharReservations } from "../../services/osharReservationsApi";
import { readPendingOsharWalletMintRecord } from "../../services/osharRealEstateApi";
import { getMyLibrary } from "../../services/libraryApi";

function ts(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function formatRowWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString();
  } catch {
    return "";
  }
}

function formatAgoLabel(iso) {
  if (!iso) return "—";
  const t = ts(iso);
  if (!t) return "—";
  const sec = Math.floor((Date.now() - t) / 1000);
  if (sec < 60) return "Just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day}d ago`;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

function osharTitle(r) {
  return r?.metadataName || r?.propertyTitle || "Oshar property";
}

function osharActivityLine(r) {
  const title = osharTitle(r);
  if (r.status === "checkout_created") return `Oshar checkout started — ${title}`;
  if (r.status === "paid_unminted") return `Oshar paid — mint pending — ${title}`;
  if (r.status === "claimed") return `Oshar mint recorded — ${title}`;
  if (r.status === "checkout_expired") return `Oshar checkout expired — ${title}`;
  if (r.status === "cancelled") return `Oshar reservation cancelled — ${title}`;
  return `Oshar activity — ${title}`;
}

function osharTone(r) {
  if (r.status === "claimed") return "green";
  if (r.status === "paid_unminted") return "gold";
  if (r.status === "checkout_created") return "blue";
  return "muted";
}

function osharIcon(r) {
  if (r.status === "claimed") return "fa-coins";
  if (r.status === "paid_unminted") return "fa-bolt";
  if (r.status === "checkout_created") return "fa-shopping-cart";
  return "fa-bookmark";
}

function osharWhen(r) {
  return r.paidAt || r.walletVerifiedAt || r.mintedAt || r.updatedAt || r.createdAt;
}

function libraryTitle(row) {
  const t = row?.edition?.title || row?.asset?.title || row?.entitlement?.assetSlug;
  return t ? String(t) : "Library item";
}

function libraryWhen(row) {
  const e = row?.entitlement;
  return e?.grantedAt || e?.createdAt || e?.updatedAt || row?.createdAt;
}

const ActivityTransmissions = () => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      setRows([]);
      setLoading(false);
      return undefined;
    }
    const ac = new AbortController();
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [resList, libBody] = await Promise.all([
          getMyOsharReservations({ signal: ac.signal }).catch(() => []),
          getMyLibrary().catch(() => null),
        ]);
        if (cancelled) return;
        const reservations = Array.isArray(resList) ? resList : [];
        const libItems = Array.isArray(libBody?.items) ? libBody.items : [];
        const pendingRec = readPendingOsharWalletMintRecord();
        const pendingTx = pendingRec && typeof pendingRec === "object" ? String(pendingRec.txHash || "").trim() : "";

        const out = [];

        if (pendingTx) {
          out.push({
            key: `pending-mint-${pendingTx}`,
            text: "Wallet mint recorded on-chain — verify sync in My Oshar Properties if units are missing.",
            whenIso: new Date().toISOString(),
            tone: "gold",
            icon: "fa-link",
          });
        }

        for (const r of reservations) {
          if (!r || r.hiddenFromDashboard) continue;
          if (r.status === "split_parent") continue;
          const w = osharWhen(r);
          const whenIso = formatRowWhen(w);
          if (!whenIso) continue;
          out.push({
            key: `oshar-${r.id || r._id || w}-${r.status}`,
            text: osharActivityLine(r),
            whenIso,
            tone: osharTone(r),
            icon: osharIcon(r),
          });
        }

        for (const row of libItems.slice(0, 12)) {
          const w = libraryWhen(row);
          if (!w) continue;
          out.push({
            key: `lib-${row?.entitlement?._id || row?.entitlement?.id || libraryTitle(row)}-${w}`,
            text: `Library access — ${libraryTitle(row)}`,
            whenIso: formatRowWhen(w) || "",
            tone: "purple",
            icon: "fa-book-open",
          });
        }

        out.sort((a, b) => ts(b.whenIso) - ts(a.whenIso));
        setRows(out.slice(0, 8));
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [isAuthenticated]);

  const empty = useMemo(() => !loading && rows.length === 0, [loading, rows.length]);

  return (
    <section className="dg-panel dg-panel--dense dg-gold-ring dg-premium-ring dg-premium-ring--interactive dg-activity dg-activity--timeline">
      <div className="dg-activity__timeline-rail" aria-hidden />
      <div className="dg-activity__head">
        <h2 className="dg-panel__title serif">Activity Transmissions</h2>
        <span className="dg-label-up dg-muted" style={{ letterSpacing: "0.12em" }}>
          {loading ? "Loading…" : "Your account"}
        </span>
      </div>
      {empty ? (
        <p className="dg-activity__empty dg-muted">No recent activity yet.</p>
      ) : (
        <ul className="dg-activity__feed">
          {rows.map((row) => (
            <li key={row.key} className="dg-activity__row dg-activity__row--live">
              <span className={`dg-activity__glyph dg-activity__glyph--${row.tone}`} aria-hidden>
                <i className={`fas ${row.icon}`} />
              </span>
              <div className="dg-activity__txt">
                <p>{row.text}</p>
                <time className="dg-activity__ago" dateTime={row.whenIso || undefined}>
                  {formatAgoLabel(row.whenIso)}
                </time>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ActivityTransmissions;
